import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PersonSummary {
  id: number;
  firstName: string | null;
  lastName: string | null;
  personType: string;
  status: "active" | "exited" | "incomplete";
  internalId: string | null;
}

interface SearchResult {
  data: PersonSummary[];
  meta: { total: number };
}

const statusDot: Record<string, string> = {
  active: "bg-green-500",
  exited: "bg-amber-500",
  incomplete: "bg-gray-400",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GLOBAL_SEARCH_ID = "alice-global-search";

export function GlobalSearchBar() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    setSelectedIndex(0);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, 300);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const { data: searchResult } = useQuery<SearchResult>({
    queryKey: ["globalSearch", debouncedQuery],
    queryFn: async () => {
      return api.get<SearchResult>(
        `/search?q=${encodeURIComponent(debouncedQuery)}&limit=5`,
      );
    },
    enabled: debouncedQuery.length >= 2,
    placeholderData: (prev) => prev,
  });

  const results = searchResult?.data ?? [];

  const handleSelect = useCallback(
    (personId: number) => {
      setIsOpen(false);
      setQuery("");
      setDebouncedQuery("");
      navigate(`/clients/${personId}`);
    },
    [navigate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || results.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && results[selectedIndex]) {
        e.preventDefault();
        handleSelect(results[selectedIndex].id);
      } else if (e.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [isOpen, results, selectedIndex, handleSelect],
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Detect platform for keyboard shortcut display
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          id={GLOBAL_SEARCH_ID}
          type="text"
          placeholder="Search clients..."
          value={query}
          onChange={(e) => {
            handleQueryChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (query.length >= 2) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className="flex h-8 w-48 rounded-md border border-input bg-background py-1 pl-8 pr-14 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-64"
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5 text-xs text-muted-foreground">
          <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">
            {isMac ? "\u2318" : "Ctrl"}
          </kbd>
          <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">
            K
          </kbd>
        </div>
      </div>

      {/* Dropdown results */}
      {isOpen && debouncedQuery.length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-card shadow-lg">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No clients found
            </div>
          ) : (
            <ul className="py-1">
              {results.map((person, index) => (
                <li key={person.id}>
                  <button
                    onClick={() => handleSelect(person.id)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors",
                      index === selectedIndex
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/50",
                    )}
                  >
                    <span
                      className={cn(
                        "h-2 w-2 shrink-0 rounded-full",
                        statusDot[person.status] ?? "bg-gray-400",
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {[person.firstName, person.lastName]
                          .filter(Boolean)
                          .join(" ") || "Unknown"}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {person.personType}
                        {person.internalId && ` - ${person.internalId}`}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {results.length > 0 && (
            <div className="border-t px-4 py-2">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate(`/clients?search=${encodeURIComponent(query)}`);
                }}
                className="text-xs text-primary hover:underline"
              >
                View all results
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
